import React from "react";
import ReactApexChart from "react-apexcharts";
import { getNiceMax } from "../utils/chartDataUtils";
import MemberBreakdownList from "./MemberBreakdownList";

const MIN_BAR_WIDTH = 50;

// SG-2164: the backend returns one {stage, memberId, name, count} row per stage per member —
// collapse those into a single total-contacts-per-member row so the shared MemberBreakdownList
// (name + progress bar out of the total across members) can render it like every other widget.
const toMemberTotals = rows => {
  if (!rows?.length) return [];
  const byMember = {};
  rows.forEach(r => {
    if (!byMember[r.memberId]) {
      byMember[r.memberId] = { memberId: r.memberId, name: r.name, count: 0 };
    }
    byMember[r.memberId].count += r.count || 0;
  });
  return Object.values(byMember);
};

const COLOR = "#3953fb";

// SG-2164: show the human-friendly stage label (e.g. "Not Contacted") instead of the raw backend
// stage code ("NOT_CONTACTED"). Prefers the team's configured displayName (stageLabel from the
// backend); falls back to humanizing the code (underscores → spaces, title-cased) for any stage
// without a displayName.
const humanizeStage = code =>
  (code || "")
    .split("_")
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
const stageDisplay = s => s.stageLabel || humanizeStage(s.stage);

// SG-2164: current snapshot of contacts by pipeline stage — intentionally NOT date-filtered
// (see Z_tasks/SG-2164-new-widgets-requirements.md "Contact Stages — date filter behavior").
// Categorical breakdown (one bar per stage). Inherent default is BAR (see getDefaultVisualization);
// users can switch to a Line chart (x-axis hidden, stage shown on hover) or a KPI numeric summary.
const ContactStagesWidget = ({ contactStages = [], memberBreakdown, visualization }) => {
  if (!contactStages.length) {
    return <p className="contactStagesEmpty">No contact stage data</p>;
  }

  const labels = contactStages.map(stageDisplay);
  const counts = contactStages.map(s => s.count);
  const { max: niceMax, tickAmount } = getNiceMax(Math.max(...counts, 1));

  const isKpi = visualization === "KPI";
  const isLine = visualization === "LINE";
  const isBar = !isLine && !isKpi; // null/undefined => bar (this widget's default)

  const chartBody = isKpi ? (
    <div className="widgetCardNumericSummary">
      {contactStages.map(s => (
        <div key={s.stage} className="widgetCardNumericSummaryRow">
          <span className="widgetCardNumericSummaryLabel">{stageDisplay(s)}</span>
          <span className="widgetCardNumericSummaryValue">{s.count}</span>
        </div>
      ))}
    </div>
  ) : (
    (() => {
      const options = {
        chart: {
          // "area" (not "line") so the connecting line renders reliably — matches RateWidget/CountWidget.
          type: isLine ? "area" : "bar",
          toolbar: { show: false },
          animations: { enabled: false },
          selection: { enabled: false },
          zoom: { enabled: false }
        },
        ...(isLine
          ? {
              stroke: { show: true, curve: "smooth", width: 2, colors: [COLOR] },
              markers: { size: 4, colors: [COLOR], strokeColors: "#fff", strokeWidth: 2, hover: { size: 5 } },
              fill: { type: "solid", opacity: 0.2 }
            }
          : {
              plotOptions: { bar: { borderRadius: 3, columnWidth: "90%", distributed: true } }
            }),
        colors: [COLOR],
        dataLabels: { enabled: false },
        legend: { show: false },
        xaxis: {
          categories: labels,
          labels: { show: false },
          axisBorder: { show: false },
          axisTicks: { show: false },
          tooltip: { enabled: false }
        },
        yaxis: {
          min: 0,
          max: niceMax,
          tickAmount,
          // Hidden to match the count/rate widgets — values are surfaced via the point tooltip on hover.
          labels: { show: false }
        },
        grid: {
          borderColor: "#e8ebee",
          strokeDashArray: 3,
          xaxis: { lines: { show: false } },
          yaxis: { lines: { show: true } }
        },
        tooltip: {
          x: { show: true },
          y: { formatter: val => `${val} contacts` }
        }
      };
      const series = [{ name: "Contacts", data: counts }];
      const chart = (
        // key forces a fresh chart instance on type change — see CallOutcomesWidget note.
        <ReactApexChart key={isLine ? "area" : "bar"} options={options} series={series} type={isLine ? "area" : "bar"} height={140} width="100%" />
      );
      // Line fits any width; bar needs a min-width so narrow columns stay legible and scroll.
      return isBar ? (
        <div className="contactStagesChartScroll" style={{ minWidth: `${labels.length * MIN_BAR_WIDTH}px` }}>
          {chart}
        </div>
      ) : (
        chart
      );
    })()
  );

  return (
    <div className="contactStagesWrap">
      <p className="contactStagesSnapshotLabel">Live snapshot</p>
      {chartBody}
      <MemberBreakdownList rows={toMemberTotals(memberBreakdown)} />
    </div>
  );
};

export default ContactStagesWidget;
